import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { FaInstagram, FaLinkedin } from 'react-icons/fa';

import axios from 'axios';

import { LuCalendarDays, LuMapPin } from 'react-icons/lu';

import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../../context/AuthContext';

import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const LIKERT_OPTIONS = [

  { label: 'Strongly disagree', value: 1 },

  { label: 'Disagree', value: 2 },

  { label: 'Neutral', value: 3 },

  { label: 'Agree', value: 4 },

  { label: 'Strongly agree', value: 5 },

];

const HOST_QUALITIES = [

  'I enjoy talking with people and getting to know them.',

  'I’m comfortable starting conversations with people I don’t know.',

  'I bring positive energy to group settings and enjoy helping people have fun.',

  'I notice when someone feels left out and help them feel included.',

  'I naturally help a group feel connected.',

  'People can count on me to follow through when I commit to something.',

];

const HOST_SKILL_OPTIONS = [

  'Yoga teacher certification',

  'Pilates instructor certification',

  'Breathwork facilitator training',

  'Personal trainer or fitness instructor certification',

  'Dance or line-dance instruction',

  'Surf instructor certification',

  'Cooking instruction or professional culinary experience',

  'Art or craft workshop instruction',

  'Sound bath facilitator',

'Certified hiking or outdoor guide',

'Wine educator or sommelier',

  'Other specialized skill or certification',

];

const INITIAL_FORM = {

  firstName: '',

  lastName: '',

  email: '',

  linkedinUrl: '',

  birthdate: '',

  address: '',

  workAuth: '',

  gender: '',

  genderSelf: '',

  orientation: '',

  orientationSelf: '',

  raceEthnicity: '',

  daysAvailable: '',

  timesAvailable: [],

  hostFreq: '',

  likerts: HOST_QUALITIES.map(() => null),

  hostSkills: [],

  hostSkillsDetails: '',

  agreeComp: false,

  agreeFinal: false,

};

const CORE_STEPS = [

  { id: 'name', number: '01', title: 'What’s your name?', type: 'name' },

  { id: 'email', number: '02', title: 'What’s your email?', type: 'email' },

  { id: 'linkedinUrl', title: 'What’s your LinkedIn profile URL?', description: 'Paste the link to your personal LinkedIn profile.', type: 'linkedin' },

  { id: 'birthdate', number: '03', title: 'What’s your birthday?', description: 'Event Hosts must be at least 18 years old.', type: 'date' },

  { id: 'address', number: '04', title: 'Where are you based in Los Angeles?', description: 'Share your neighborhood or address so we know which events are practical for you.', type: 'text', placeholder: 'Neighborhood or address' },

  { id: 'workAuth', number: '05', title: 'Are you legally authorized to work in the United States?', type: 'choice', options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }] },

  { id: 'demographics', number: '06', title: 'Optional demographic information', description: 'These questions are optional and are not used in selection decisions.', type: 'demographics', optional: true },

  { id: 'daysAvailable', number: '07', title: 'Which days are you usually available to host?', type: 'choice', options: [{ label: 'Weekdays', value: 'weekdays' }, { label: 'Weekends', value: 'weekends' }, { label: 'Both', value: 'both' }] },

  { id: 'timesAvailable', number: '08', title: 'What times are you usually available?', description: 'Select all that apply.', type: 'multiChoice', options: [{ label: 'Mornings', value: 'mornings' }, { label: 'Afternoons', value: 'afternoons' }, { label: 'Evenings', value: 'evenings' }] },

  { id: 'hostFreq', number: '09', title: 'How often could you realistically host?', description: 'We’re looking for people who can host consistently.', type: 'choice', options: [{ label: 'Once a month', value: '1_per_month' }, { label: 'Twice a month', value: '2_per_month' }, { label: 'Three times a month', value: '3_per_month' }, { label: 'Four or more times a month', value: '4_plus' }] },

];

const STEPS = [

  ...CORE_STEPS,

  ...HOST_QUALITIES.map((title, index) => ({

    id: 'hostQuality' + index,

    number: String(index + 10).padStart(2, '0'),

    title,

    description: 'Choose how much you agree with this statement.',

    type: 'likert',

    index,

  })),

  { id: 'hostSkills', number: '16', title: 'Do you have any specialized skills or certifications?', description: 'Select any that apply. We may reach out about events that require relevant training or expertise.', type: 'skills', optional: true },

  { id: 'agreeComp', number: '17', title: 'Pay and event expenses', description: 'Hosts are paid $40 per hour. Tribe also provides payment for event-related items and expenses needed during events.', type: 'ack', field: 'agreeComp', checkboxLabel: 'I understand and agree.' },

  { id: 'agreeFinal', number: '18', title: 'Before you submit', description: 'Submitting an application does not guarantee acceptance. If selected, the next steps include an interview and training.', type: 'ack', field: 'agreeFinal', checkboxLabel: 'I confirm that the information in my application is accurate.' },

].map((step, index) => ({ ...step, number: String(index + 1).padStart(2, '0') }));

function normalizeLinkedinUrl(value) {
  if (typeof value !== 'string') return '';
  const input = value.trim();
  if (!input || input.length > 2048 || /[\s\\]/.test(input)) return '';
  try {
    const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(input) ? input : `https://${input}`);
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port) return '';
    const host = url.hostname.toLowerCase();
    if (!['linkedin.com', 'www.linkedin.com', 'm.linkedin.com'].includes(host) && !/^[a-z]{2}\.linkedin\.com$/.test(host)) return '';
    const match = url.pathname.match(/^\/in\/([^/]+)\/?$/);
    if (!match) return '';
    const slug = decodeURIComponent(match[1]);
    if (!/^[\p{L}\p{N}._~-]+$/u.test(slug) || slug === '.' || slug === '..') return '';
    return `https://www.linkedin.com/in/${encodeURIComponent(slug)}`;
  } catch {
    return '';
  }
}

function normalizeEmail(value) {

  return String(value || '').trim().toLowerCase();

}

function calculateAge(birthdate) {

  if (!birthdate) return null;

  const dob = new Date(birthdate + 'T00:00:00');

  if (Number.isNaN(dob.getTime())) return null;

  const today = new Date();

  let age = today.getFullYear() - dob.getFullYear();

  const month = today.getMonth() - dob.getMonth();

  if (month < 0 || (month === 0 && today.getDate() < dob.getDate())) age -= 1;

  return age;

}

function getTokenFromStorage() {

  return localStorage.getItem('accessToken') || localStorage.getItem('token') || localStorage.getItem('jwt') || '';

}

function authHeaders(token) {

  return token ? { Authorization: 'Bearer ' + token } : {};

}

function hasAnswer(step, form) {

  if (step.type === 'name') return Boolean(form.firstName.trim() || form.lastName.trim());

  if (step.type === 'demographics') return Boolean(form.gender || form.orientation || form.raceEthnicity || form.genderSelf.trim() || form.orientationSelf.trim());

  if (step.type === 'likert') return typeof form.likerts[step.index] === 'number';

  if (step.type === 'multiChoice' || step.type === 'skills') return Array.isArray(form[step.id]) && form[step.id].length > 0;

  if (step.type === 'ack') return Boolean(form[step.field]);

  return Boolean(String(form[step.id] || '').trim());

}

export default function HostRecruitmentSurvey() {

  const { currentUser } = useContext(AuthContext) || {};

  const token = getTokenFromStorage();

  const navigate = useNavigate();

  const [screen, setScreen] = useState('intro');

  const [stepIndex, setStepIndex] = useState(0);

  const [form, setForm] = useState(() => ({

    ...INITIAL_FORM,

    firstName: currentUser?.firstName || '',

    lastName: currentUser?.lastName || '',

    email: currentUser?.email || '',

  }));

  const [loading, setLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState('');

  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const inputRef = useRef(null);

  const step = STEPS[stepIndex];

  useEffect(() => {

    if (screen !== 'form') return undefined;

    const timer = window.setTimeout(() => inputRef.current?.focus?.(), 80);

    return () => window.clearTimeout(timer);

  }, [screen, stepIndex]);

  useEffect(() => {

    let mounted = true;

    const lookupEmail = normalizeEmail(currentUser?.email || '');

    async function hydrate() {

      if (!lookupEmail) {

        if (mounted) setLoading(false);

        return;

      }

      try {

        const response = await axios.get(

          API + '/host-onboarding/by-email?email=' + encodeURIComponent(lookupEmail),

          { headers: authHeaders(token) }

        );

        const application = response.data?.application;

        const saved = application?.survey?.answers || {};

        if (!mounted) return;

        setForm((prev) => ({

          ...prev,

          ...saved,

          firstName: saved.firstName || currentUser?.firstName || prev.firstName,

          lastName: saved.lastName || currentUser?.lastName || prev.lastName,

          email: saved.email || lookupEmail,

          linkedinUrl: application?.linkedinUrl || saved.linkedinUrl || prev.linkedinUrl,

          likerts: Array.isArray(saved.likerts) && saved.likerts.length === HOST_QUALITIES.length

            ? saved.likerts

            : INITIAL_FORM.likerts,

          timesAvailable: Array.isArray(saved.timesAvailable) ? saved.timesAvailable : [],

          hostSkills: Array.isArray(saved.hostSkills) ? saved.hostSkills : [],

          gender: Array.isArray(saved.gender) ? (saved.gender[0] || '') : (saved.gender || ''),

          genderSelf: saved.genderSelf || '',

          orientation: saved.orientation || '',

          orientationSelf: saved.orientationSelf || '',

          raceEthnicity: saved.raceEthnicity || '',

        }));

        if (application?.survey?.submittedAt) setScreen('success');

      } catch (err) {

        // Applicants can still complete the public form if lookup is unavailable.

      } finally {

        if (mounted) setLoading(false);

      }

    }

    hydrate();

    return () => { mounted = false; };

  }, [currentUser?.email, currentUser?.firstName, currentUser?.lastName, token]);

  const canContinue = useMemo(() => {

    if (!step) return false;

    if (step.optional) return true;

    if (step.type === 'name') return Boolean(form.firstName.trim() && form.lastName.trim());

    if (step.type === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

    if (step.type === 'linkedin') return Boolean(normalizeLinkedinUrl(form.linkedinUrl));

    if (step.type === 'date') {

      const age = calculateAge(form.birthdate);

      return Number.isFinite(age) && age >= 18;

    }

    if (step.type === 'likert') return typeof form.likerts[step.index] === 'number';

    if (step.type === 'multiChoice') return Array.isArray(form[step.id]) && form[step.id].length > 0;

    if (step.type === 'ack') return Boolean(form[step.field]);

    return Boolean(String(form[step.id] || '').trim());

  }, [form, step]);

  function updateField(field, value) {

    setError('');

    setForm((prev) => ({ ...prev, [field]: value }));

  }

  function toggleMultiChoice(field, value) {

    setError('');

    setForm((prev) => {

      const current = Array.isArray(prev[field]) ? prev[field] : [];

      return {

        ...prev,

        [field]: current.includes(value)

          ? current.filter((item) => item !== value)

          : [...current, value],

      };

    });

  }

  function startApplication() {

    setScreen('form');

    setStepIndex(0);

    setError('');

  }

  function previousStep() {

    if (submitting) return;

    setError('');

    if (stepIndex === 0) {

      setScreen('intro');

      return;

    }

    setStepIndex((prev) => prev - 1);

  }

  function nextStep() {

    if (!canContinue || submitting) return;

    if (step.type === 'linkedin') {

      updateField('linkedinUrl', normalizeLinkedinUrl(form.linkedinUrl));

    }

    setError('');

    if (stepIndex < STEPS.length - 1) {

      setStepIndex((prev) => prev + 1);

      return;

    }

    setShowSubmitConfirm(true);

  }

  function selectChoice(value) {

    if (step.type === 'likert') {

      setError('');

      setForm((prev) => {

        const likerts = [...prev.likerts];

        likerts[step.index] = value;

        return { ...prev, likerts };

      });

      return;

    }

    updateField(step.id, value);

  }

  function closeSubmitConfirm() {

    if (!submitting) setShowSubmitConfirm(false);

  }

  async function submitApplication() {

    if (submitting) return;

    const linkedinUrl = normalizeLinkedinUrl(form.linkedinUrl);

    if (!linkedinUrl) {

      setShowSubmitConfirm(false);

      setScreen('form');

      setStepIndex(STEPS.findIndex((item) => item.id === 'linkedinUrl'));

      setError('A valid LinkedIn profile URL is required.');

      return;

    }

    setSubmitting(true);

    setError('');

    try {

      const sum = form.likerts.reduce((total, value) => total + (Number(value) || 0), 0);

      let bump = 0;

      if (form.hostFreq === '1_per_month') bump -= 10;

      if (form.hostFreq === '3_per_month') bump += 4;

      if (form.hostFreq === '4_plus') bump += 8;

      if (form.daysAvailable === 'both') bump += 2;

      if (form.daysAvailable === 'weekends') bump += 1;

      if (form.timesAvailable.includes('evenings')) bump += 2;

      const score = form.workAuth === 'yes'

        ? Math.max(0, Math.min(100, Math.round((sum / (HOST_QUALITIES.length * 5)) * 100) + bump))

        : 0;

      const answers = {

        ...form,

        firstName: form.firstName.trim(),

        lastName: form.lastName.trim(),

        email: normalizeEmail(form.email),

        linkedinUrl,

        address: form.address.trim(),

        hardFail: form.workAuth === 'no',

        hardFailReason: form.workAuth === 'no' ? 'work_not_authorized' : '',

      };

      await axios.post(API + '/host-onboarding/survey', { answers, score });

      setScreen('success');

      setShowSubmitConfirm(false);

      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {

      setShowSubmitConfirm(false);

      setError(err?.response?.data?.message || err?.message || 'Failed to submit your application. Please try again.');

    } finally {

      setSubmitting(false);

    }

  }

  function handleKeyDown(event) {

    if (showSubmitConfirm && event.key === 'Escape') {

      event.preventDefault();

      closeSubmitConfirm();

      return;

    }

    if (showSubmitConfirm) return;

    if (screen === 'intro' && event.key === 'Enter') {

      event.preventDefault();

      startApplication();

      return;

    }

    if (screen !== 'form' || event.key !== 'Enter') return;

    if (event.target?.tagName === 'TEXTAREA' && !event.metaKey && !event.ctrlKey) return;

    if (canContinue) {

      event.preventDefault();

      nextStep();

    }

  }

  function handleDone() {

    const desktop = window.matchMedia('(min-width: 900px)').matches ||

      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    navigate(desktop ? '/' : '/events');

  }

  if (loading) {

    return (

      <main className="referralApplicationPage" style={styles.formPage}>

        <ResponsiveStyles />

        <Header />

        <section style={styles.successShell}><p style={styles.successCopy}>Loading your application…</p></section>

      </main>

    );

  }

  if (screen === 'intro') {

    return (

      <main className="referralApplicationLanding" style={styles.landingPage} onKeyDown={handleKeyDown}>

        <ResponsiveStyles />

        <header className="referralLandingHeader" style={styles.landingHeader}>

          <TribeLogo className="referralLogoBlack" style={styles.landingLogo} aria-label="Tribe" />

          <div style={styles.landingHeaderTitle}>EVENT HOST APPLICATION</div>

        </header>

        <div className="referralLandingAmbient" style={styles.landingAmbient} aria-hidden="true">

          <div className="referralAmbient referralAmbientProspect" style={styles.ambientProspect}>

            <span style={styles.ambientAvatar}>T</span>

            <div style={styles.ambientTextStack}><strong>Warm welcome</strong><small>First-time guest feels included</small></div>

            <span style={styles.ambientStatus}>HOST</span>

          </div>

          <div className="referralAmbient referralAmbientMessage" style={styles.ambientMessage}>

            <span style={styles.ambientDot} /><p>Hey, welcome! Want me to introduce you around?</p>

          </div>

          <div className="referralAmbient referralAmbientQualified" style={styles.ambientQualified}>

            <span style={styles.ambientCheck}>✓</span>

            <div style={styles.ambientTextStack}><strong>Guests checked in</strong><small>12 of 12 arrived</small></div>

          </div>

          <div className="referralAmbient referralAmbientEarnings" style={styles.ambientEarnings}>

            <span style={styles.ambientSmallLabel}>HOST PAY</span>

            <div style={styles.ambientEarningsRow}><strong>$40</strong><i /><div><b>per hour</b><span>plus event expenses</span></div></div>

          </div>

          <div className="referralAmbient referralAmbientCode" style={styles.ambientCode}>

            <span style={styles.ambientSmallLabel}>ACTIVITY LEAD</span><strong>YOGA</strong><small>Movement & wellness</small>

          </div>

        </div>

        <section className="referralLandingContent" style={styles.landingContent}>

          <h1 style={styles.landingTitle}>Tribe Host Application</h1>

          <p style={styles.landingCopy}>We’re looking to hire friendly, outgoing people who can make guests feel welcome, help people connect, and guide great experiences.</p>

          <div style={styles.landingActions}>

            <button type="button" onClick={startApplication} style={styles.landingButton}>Start application</button>

            <span className="referralEnterHint" style={styles.landingEnterHint}>press <strong>Enter ↵</strong></span>

          </div>

        </section>

        <footer className="referralLandingFooter" style={styles.landingFooter}>

          <div style={styles.landingLegal}>© 2026 Tribe, Inc. All rights reserved.</div>

          <div style={styles.landingSocials}>

            <a href="https://www.instagram.com/jointribe.io/" target="_blank" rel="noreferrer" aria-label="Instagram" style={styles.landingSocialLink}><FaInstagram /></a>

            <a href="https://www.linkedin.com/company/82302259" target="_blank" rel="noreferrer" aria-label="LinkedIn" style={styles.landingSocialLink}><FaLinkedin /></a>

          </div>

        </footer>

      </main>

    );

  }

  if (screen === 'success') {

    return (

      <main className="referralApplicationPage" style={styles.formPage}>

        <ResponsiveStyles />

        <Header minimal />

        <section style={styles.successShell}>

          <div style={styles.successMark}>✓</div>

          <h1 style={styles.successTitle}>Application received.</h1>

          <p style={styles.successCopy}>Thanks for applying to host with Tribe. We’ll review your application and reach out by email if we’d like to move forward with an interview.</p>

          <button type="button" onClick={handleDone} style={{ ...styles.successButton, border: 0, cursor: 'pointer', font: 'inherit' }}>Back to Tribe</button>

        </section>

        <ApplicationFooter />

      </main>

    );

  }

  const continueLabel = submitting

    ? 'Submitting…'

    : stepIndex === STEPS.length - 1

      ? 'Review application'

      : step.optional && !hasAnswer(step, form)

        ? 'Skip'

        : 'Continue';

  const continueButton = (

    <button type="button" onClick={nextStep} disabled={!canContinue || submitting} style={{ ...styles.continueButton, ...(!canContinue || submitting ? styles.continueButtonDisabled : {}) }}>

      {continueLabel}

    </button>

  );

  return (

    <main className="referralApplicationPage" style={styles.formPage} onKeyDown={handleKeyDown}>

      <ResponsiveStyles />

      <Header />

      <div className="referralFormAmbient" style={styles.formAmbient} aria-hidden="true">

        <div className="referralFormAmbientCard referralFormAmbientProspect" style={{ ...styles.formAmbientCard, ...styles.formAmbientProspect }}>

          <span style={styles.formAmbientAvatar}>T</span>

          <div style={styles.formAmbientCopy}><strong>Event host</strong><small>Los Angeles · flexible schedule</small></div>

        </div>

        <div className="referralFormAmbientCard referralFormAmbientQualified" style={{ ...styles.formAmbientCard, ...styles.formAmbientQualified }}>

          <span style={styles.formAmbientCheck}>✓</span>

          <div style={styles.formAmbientCopy}><strong>Guest check-in</strong><small>12 of 12 guests welcomed</small></div>

        </div>

        <div className="referralFormAmbientCard referralFormAmbientCode" style={{ ...styles.formAmbientCard, ...styles.formAmbientCode }}>

          <span style={styles.formAmbientLabel}>ACTIVITY LEAD</span><strong>Yoga, walks, and more</strong>

        </div>

      </div>

      <section style={styles.questionShell}>

        <div className="referralQuestionContent" style={styles.questionContent}>

          <div style={styles.questionNumber}>{step.number} →</div>

          <h1 style={styles.questionTitle}>{step.title}</h1>

          {step.description ? <p style={styles.questionDescription}>{step.description}</p> : null}

          <div style={styles.answerArea}>

            {renderAnswer({ step, form, inputRef, updateField, selectChoice, toggleMultiChoice })}

          </div>

          {error ? <div style={styles.errorBox}>{error}</div> : null}

          <div className="referralDesktopActions" style={styles.desktopActions}>

            <button type="button" onClick={previousStep} disabled={submitting} style={styles.backButton}>Back</button>

            {continueButton}

          </div>

        </div>

      </section>

      <div className="referralMobileControls" style={styles.bottomControls}>

        <div style={styles.bottomControlsInner}>

          <button type="button" onClick={previousStep} disabled={submitting} style={styles.backButton}>Back</button>

          {continueButton}

        </div>

      </div>

      {showSubmitConfirm ? (

        <div className="referralSubmitModalBackdrop" style={styles.submitModalBackdrop} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeSubmitConfirm(); }}>

          <div role="dialog" aria-modal="true" aria-labelledby="host-submit-confirm-title" style={styles.submitModal}>

            <div style={styles.submitModalEyebrow}>SUBMIT APPLICATION</div>

            <h2 id="host-submit-confirm-title" style={styles.submitModalTitle}>Ready to send it in?</h2>

            <p style={styles.submitModalCopy}>We’ll send your host application to the Tribe team for review.</p>

            <div style={styles.submitModalActions}>

              <button type="button" onClick={closeSubmitConfirm} disabled={submitting} style={styles.submitModalCancelButton}>Go back</button>

              <button type="button" onClick={submitApplication} disabled={submitting} style={{ ...styles.submitModalConfirmButton, ...(submitting ? styles.continueButtonDisabled : {}) }}>{submitting ? 'Submitting…' : 'Submit application'}</button>

            </div>

          </div>

        </div>

      ) : null}

      <ApplicationFooter />

    </main>

  );

}

function ResponsiveStyles() {

  return (

    <style>

      {`

        .referralLogoBlack :is(path,circle,rect,polygon,polyline,ellipse) {

          fill: #050505 !important;

        }

        .referralLogoBlack [fill="none"] {

          fill: none !important;

        }

        .referralLogoBlack * {

          stroke: none !important;

        }

        .referralApplicationLanding {

          isolation: isolate;

        }

        .referralApplicationLanding::before {

          content: "";

          position: absolute;

          inset: -50%;

          z-index: -1;

          pointer-events: none;

          opacity: 0.05;

          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180' viewBox='0 0 180 180'%3E%3Cfilter id='grain'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.92' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Cpath fill='%23000' filter='url(%23grain)' d='M0 0h180v180H0z'/%3E%3C/svg%3E");

          background-repeat: repeat;

          background-size: 180px 180px;

        }

        .hostSkillGrid {

          grid-template-columns: repeat(2, minmax(0, 1fr));

          gap: 10px;

        }

        @media (max-width: 560px) {

          .hostSkillGrid { grid-template-columns: 1fr; }

        }

        .referralQuestionContent input:focus,

        .referralQuestionContent textarea:focus {

          box-shadow: none !important;

        }

        .referralQuestionContent div:focus-within {

          border-bottom-color: #171717;

        }

        .referralFormAmbient {

          pointer-events: none;

          user-select: none;

        }

        .referralFormAmbientCard {

          position: absolute;

          border: 1px solid rgba(17,17,17,.035);

          background: rgba(250,250,249,.72);

          box-shadow: 0 10px 28px rgba(0,0,0,.012);

          backdrop-filter: blur(4px);

          -webkit-backdrop-filter: blur(4px);

        }

        .referralFormAmbientCard strong,

        .referralFormAmbientCard small {

          display: block;

        }

        .referralFormAmbientCard strong {

          color: #777772;

          font-size: 9px;

          font-weight: 650;

        }

        .referralFormAmbientCard small {

          color: #a0a09b;

          font-size: 7px;

        }

        @media (max-width: 900px) {

          .referralFormAmbientProspect {

            left: 3% !important;

          }

          .referralFormAmbientQualified {

            right: 3% !important;

          }

        }

        @media (max-width: 680px) {

          .referralFormAmbientProspect,

          .referralFormAmbientQualified,

          .referralFormAmbientCode {

            opacity: .16 !important;

          }

        }

        @media (max-width: 520px) {

          .referralFormAmbientProspect,

          .referralFormAmbientQualified,

          .referralFormAmbientCode {

            display: none !important;

          }

        }

        .referralLandingAmbient {

          pointer-events: none;

          user-select: none;

        }

        .referralAmbient {

          position: absolute;

          border: 1px solid rgba(17,17,17,.045);

          background: rgba(250,250,249,.74);

          box-shadow: 0 10px 34px rgba(0,0,0,.018);

          backdrop-filter: blur(4px);

          -webkit-backdrop-filter: blur(4px);

        }

        .referralAmbient strong,

        .referralAmbient small {

          display: block;

        }

        .referralAmbient strong {

          color: #656560;

          font-size: 10px;

          font-weight: 650;

        }

        .referralAmbient small {

          color: #969691;

          font-size: 8px;

        }

        .referralAmbientMessage p {

          margin: 0;

          font-size: 9px;

          line-height: 1.4;

        }

        .referralAmbientEarnings > div > strong {

          color: #5f5f5a;

          font-size: 24px;

          line-height: 1;

          letter-spacing: -.04em;

        }

        .referralAmbientEarnings > div > i {

          width: 1px;

          height: 28px;

          background: #dfdfdb;

        }

        .referralAmbientEarnings > div > div {

          display: grid;

          gap: 2px;

        }

        .referralAmbientEarnings > div > div b {

          color: #666661;

          font-size: 15px;

          line-height: 1;

        }

        .referralAmbientEarnings > div > div span {

          color: #999994;

          font-size: 7px;

        }

        .referralAmbientCode > strong {

          margin-top: 6px;

          color: #686863;

          font-size: 14px;

          letter-spacing: .035em;

        }

        .referralAmbientCode > small {

          margin-top: 4px;

        }

        @media (max-width: 900px) {

          .referralAmbientProspect {

            left: 5% !important;

          }

          .referralAmbientMessage {

            right: 4% !important;

          }

          .referralAmbientQualified {

            right: 7% !important;

          }

          .referralAmbientEarnings {

            left: 7% !important;

          }

        }

        @media (max-width: 760px) {

          .referralApplicationLanding {

            padding: 24px 22px 18px !important;

          }

          .referralLandingHeader {

            min-height: 56px !important;

            padding: 0 !important;

            align-items: center !important;

          }

          .referralLandingHeader > div:last-child {

            font-size: 10px !important;

          }

          .referralLandingContent {

            width: calc(100% - 44px) !important;

          }

          .referralLandingAmbient {

            opacity: .48;

          }

          .referralAmbientProspect,

          .referralAmbientMessage,

          .referralAmbientQualified,

          .referralAmbientCode {

            display: none !important;

          }

          .referralAmbientEarnings {

            left: 50% !important;

            top: 16% !important;

            width: 250px !important;

            transform: translateX(-50%) !important;

          }

          .referralLandingFooter {

            min-height: 36px !important;

            padding: 0 !important;

            align-items: center !important;

          }

        }

        @media (max-width: 520px) {

          .referralLandingHeader > svg {

            width: 84px !important;

          }

          .referralLandingHeader > div:last-child {

            font-size: 9px !important;

            letter-spacing: .035em !important;

          }

          .referralLandingFooter > div:first-child {

            max-width: 180px;

            line-height: 1.35;

          }

        }

        @media (max-width: 760px) {

          .referralApplicationPage {

            padding: 24px 22px 18px !important;

          }

          .referralApplicationHeader {

            min-height: 56px !important;

            gap: 12px !important;

            padding: 0 !important;

            align-items: center !important;

          }

          .referralApplicationHeader > svg {

            width: 92px !important;

          }

          .referralApplicationHeader > div:last-child {

            font-size: 10px !important;

          }

          .referralApplicationFooter {

            min-height: 36px !important;

            padding: 0 !important;

            align-items: center !important;

          }

        }

        @media (max-width: 520px) {

          .referralApplicationHeader > svg {

            width: 84px !important;

          }

          .referralApplicationHeader > div:last-child {

            font-size: 9px !important;

            letter-spacing: .035em !important;

          }

          .referralApplicationFooter > div:first-child {

            max-width: 180px;

            line-height: 1.35;

          }

        }

        @media (max-width: 440px) {

          .referralEnterHint {

            display: none !important;

          }

          .referralHeader {

            height: auto !important;

          }

          .referralQuestionContent {

            width: 100% !important;

            padding: 7vh 20px 108px !important;

          }

          .referralQuestionContent h1 {

            max-width: 440px !important;

          }

          .referralDesktopActions {

            display: none !important;

          }

          .referralQuestionContent input,

          .referralQuestionContent textarea {

            max-width: 100% !important;

          }

          .referralNameGrid {

            grid-template-columns: 1fr !important;

          }

          .referralMobileControls {

            display: block !important;

            position: fixed !important;

            left: 0 !important;

            right: 0 !important;

            bottom: 0 !important;

            padding-bottom: env(safe-area-inset-bottom) !important;

          }

        }

      `}

    </style>

  );

}

function ApplicationFooter() {

  return (

    <footer

      className="referralApplicationFooter"

      style={styles.applicationFooter}

    >

      <div style={styles.applicationLegal}>

        © 2026 Tribe, Inc. All rights reserved.

      </div>

      <div style={styles.applicationSocials}>

        <a

          href="https://www.instagram.com/jointribe.io/"

          target="_blank"

          rel="noreferrer"

          aria-label="Instagram"

          style={styles.applicationSocialLink}

        >

          <FaInstagram />

        </a>

        <a

          href="https://www.linkedin.com/company/82302259"

          target="_blank"

          rel="noreferrer"

          aria-label="LinkedIn"

          style={styles.applicationSocialLink}

        >

          <FaLinkedin />

        </a>

      </div>

    </footer>

  );

}

function Header({ minimal = false }) {

  return (

    <header className="referralHeader referralApplicationHeader" style={styles.applicationHeader}>

      <TribeLogo className="referralLogoBlack" style={styles.applicationHeaderLogo} aria-label="Tribe" />

      <div style={styles.applicationHeaderTitle}>EVENT HOST APPLICATION</div>

    </header>

  );

}

function renderAnswer({ step, form, inputRef, updateField, selectChoice, toggleMultiChoice }) {

  if (step.type === 'demographics') {

    return (

      <div style={{ display: 'grid', gap: 22 }}>

        <div>

          <label style={styles.fieldLabel} htmlFor="gender">Gender</label>

          <select id="gender" value={form.gender} onChange={(event) => updateField('gender', event.target.value)} style={styles.textInput}>

            <option value="">Select…</option>

            <option value="Woman">Woman</option>

            <option value="Man">Man</option>

            <option value="Non-binary">Non-binary</option>

            <option value="Prefer to self-describe">Prefer to self-describe</option>

            <option value="Prefer not to say">Prefer not to say</option>

          </select>

          {form.gender === 'Prefer to self-describe' ? (

            <input type="text" value={form.genderSelf} onChange={(event) => updateField('genderSelf', event.target.value)} placeholder="Self-describe" style={{ ...styles.textInput, marginTop: 8 }} />

          ) : null}

        </div>

        <div>

          <label style={styles.fieldLabel} htmlFor="orientation">Sexual orientation</label>

          <select id="orientation" value={form.orientation} onChange={(event) => updateField('orientation', event.target.value)} style={styles.textInput}>

            <option value="">Select…</option>

            <option value="Heterosexual">Heterosexual</option>

            <option value="Gay or lesbian">Gay or lesbian</option>

            <option value="Bisexual">Bisexual</option>

            <option value="Queer">Queer</option>

            <option value="Prefer to self-describe">Prefer to self-describe</option>

            <option value="Prefer not to say">Prefer not to say</option>

          </select>

          {form.orientation === 'Prefer to self-describe' ? (

            <input type="text" value={form.orientationSelf} onChange={(event) => updateField('orientationSelf', event.target.value)} placeholder="Self-describe" style={{ ...styles.textInput, marginTop: 8 }} />

          ) : null}

        </div>

        <div>

          <label style={styles.fieldLabel} htmlFor="raceEthnicity">Race / ethnicity</label>

          <select id="raceEthnicity" value={form.raceEthnicity} onChange={(event) => updateField('raceEthnicity', event.target.value)} style={styles.textInput}>

            <option value="">Select…</option>

            <option value="Black or African American">Black or African American</option>

            <option value="Hispanic or Latino">Hispanic or Latino</option>

            <option value="Asian">Asian</option>

            <option value="White">White</option>

            <option value="Native American or Alaska Native">Native American or Alaska Native</option>

            <option value="Native Hawaiian or Pacific Islander">Native Hawaiian or Pacific Islander</option>

            <option value="Multiracial">Multiracial</option>

            <option value="Prefer not to say">Prefer not to say</option>

          </select>

        </div>

      </div>

    );

  }

  if (step.type === 'name') {

    return (

      <div className="referralNameGrid" style={styles.nameGrid}>

        <div>

          <label style={styles.fieldLabel}>First name</label>

          <input ref={inputRef} type="text" value={form.firstName} onChange={(event) => updateField('firstName', event.target.value)} placeholder="First name" autoComplete="given-name" style={styles.textInput} />

        </div>

        <div>

          <label style={styles.fieldLabel}>Last name</label>

          <input type="text" value={form.lastName} onChange={(event) => updateField('lastName', event.target.value)} placeholder="Last name" autoComplete="family-name" style={styles.textInput} />

        </div>

      </div>

    );

  }

  if (step.type === 'email') {

    return <input ref={inputRef} type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} placeholder="name@example.com" autoComplete="email" style={styles.textInput} />;

  }

  if (step.type === 'linkedin') {

    const invalid = Boolean(form.linkedinUrl.trim()) && !normalizeLinkedinUrl(form.linkedinUrl);

    return (

      <>

        <div style={styles.iconInputWrap}>

          <FaLinkedin aria-hidden="true" style={styles.inputLeadingIcon} />

          <input ref={inputRef} type="url" inputMode="url" value={form.linkedinUrl} onChange={(event) => updateField('linkedinUrl', event.target.value)} placeholder="https://www.linkedin.com/in/your-name" aria-label="LinkedIn profile URL" required autoCapitalize="none" autoCorrect="off" spellCheck={false} aria-invalid={invalid} aria-describedby={invalid ? 'linkedin-url-error' : undefined} style={styles.iconTextInput} />

        </div>

        {invalid ? <p id="linkedin-url-error" style={{ ...styles.questionDescription, color: '#861f1f' }}>Enter a personal profile link, such as linkedin.com/in/your-name.</p> : null}

      </>

    );

  }

  if (step.type === 'date') {

    return (

      <div style={styles.iconInputWrap}>

        <LuCalendarDays aria-hidden="true" style={styles.inputLeadingIcon} />

        <input ref={inputRef} type="date" value={form.birthdate} onChange={(event) => updateField('birthdate', event.target.value)} autoComplete="bday" style={styles.iconTextInput} />

      </div>

    );

  }

  if (step.type === 'text') {

    return (

      <div style={styles.iconInputWrap}>

        <LuMapPin aria-hidden="true" style={styles.inputLeadingIcon} />

        <input ref={inputRef} type="text" value={form[step.id]} onChange={(event) => updateField(step.id, event.target.value)} placeholder={step.placeholder} style={styles.iconTextInput} />

      </div>

    );

  }

  if (step.type === 'choice' || step.type === 'likert') {

    const options = step.type === 'likert' ? LIKERT_OPTIONS : step.options;

    const selectedValue = step.type === 'likert' ? form.likerts[step.index] : form[step.id];

    return (

      <div style={styles.choiceList}>

        {options.map((option, index) => {

          const value = typeof option === 'string' ? option : option.value;

          const label = typeof option === 'string' ? option : option.label;

          const selected = selectedValue === value;

          return (

            <button key={value} type="button" onClick={() => selectChoice(value)} style={{ ...styles.choiceButton, ...(selected ? styles.choiceButtonSelected : {}) }}>

              <span style={{ ...styles.choiceKey, ...(selected ? styles.choiceKeySelected : {}) }}>{String.fromCharCode(65 + index)}</span>

              <span style={styles.choiceText}>{label}</span>

              {selected ? <span style={styles.check}>✓</span> : null}

            </button>

          );

        })}

      </div>

    );

  }

  if (step.type === 'multiChoice') {

    const selectedValues = Array.isArray(form[step.id]) ? form[step.id] : [];

    return (

      <div style={styles.choiceList}>

        {step.options.map((option, index) => {

          const value = typeof option === 'string' ? option : option.value;

          const label = typeof option === 'string' ? option : option.label;

          const selected = selectedValues.includes(value);

          return (

            <button key={value} type="button" onClick={() => toggleMultiChoice(step.id, value)} style={{ ...styles.choiceButton, ...(selected ? styles.choiceButtonSelected : {}) }}>

              <span style={{ ...styles.choiceKey, ...(selected ? styles.choiceKeySelected : {}) }}>{String.fromCharCode(65 + index)}</span>

              <span style={styles.choiceText}>{label}</span>

              {selected ? <span style={styles.check}>✓</span> : null}

            </button>

          );

        })}

      </div>

    );

  }

  if (step.type === 'skills') {

    return (

      <div>

        <div className="hostSkillGrid" style={{ ...styles.choiceList, width: '100%', maxWidth: 'none' }}>

          {HOST_SKILL_OPTIONS.map((option) => {

            const selected = form.hostSkills.includes(option);

            return (

              <button key={option} type="button" onClick={() => toggleMultiChoice('hostSkills', option)} style={{ ...styles.choiceButton, ...(selected ? styles.choiceButtonSelected : {}) }}>

                <span style={styles.choiceText}>{option}</span>

                {selected ? <span style={styles.check}>✓</span> : null}

              </button>

            );

          })}

        </div>

        <div style={{ marginTop: 24 }}>

          <label style={styles.fieldLabel} htmlFor="hostSkillsDetails">Which specific skills, certifications, or training do you have? (optional)</label>

          <textarea id="hostSkillsDetails" value={form.hostSkillsDetails} onChange={(event) => updateField('hostSkillsDetails', event.target.value)} placeholder="For example: yoga teacher certification, CPR training, or experience leading a specialized activity." rows={4} style={styles.textarea} />

        </div>

      </div>

    );

  }

  if (step.type === 'ack') {

    return (

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 15, lineHeight: 1.5, color: '#292929', cursor: 'pointer' }}>

        <input type="checkbox" checked={Boolean(form[step.field])} onChange={(event) => updateField(step.field, event.target.checked)} style={{ width: 19, height: 19, marginTop: 2, accentColor: '#111' }} />

        <span>{step.checkboxLabel}</span>

      </label>

    );

  }

  return null;

}

const styles = {

  landingPage: {

    minHeight: '100vh',

    position: 'relative',

    overflow: 'hidden',

    display: 'grid',

    gridTemplateRows: 'auto 1fr auto',

    padding: '30px 44px 24px',

    background:

      'radial-gradient(circle at 50% 47%, rgba(246,246,244,.95) 0%, rgba(251,251,250,.72) 28%, rgba(255,255,255,0) 58%), #fff',

    color: '#151515',

    boxSizing: 'border-box',

    fontFamily:

      'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  },

  landingHeader: {

    position: 'relative',

    zIndex: 6,

    minHeight: 56,

    padding: 0,

    display: 'flex',

    alignItems: 'center',

    justifyContent: 'space-between',

    boxSizing: 'border-box',

  },

  landingLogo: {

    width: 92,

    height: 'auto',

    display: 'block',

  },

  landingHeaderTitle: {

    color: '#111',

    fontSize: 12,

    fontWeight: 700,

    letterSpacing: '.055em',

    whiteSpace: 'nowrap',

  },

  landingAmbient: {

    position: 'absolute',

    zIndex: 1,

    inset: '88px 0 74px',

  },

  ambientProspect: {

    left: '13%',

    top: '22%',

    display: 'flex',

    alignItems: 'center',

    gap: 10,

    width: 226,

    padding: '10px 12px',

    borderRadius: 14,

    color: '#6d6d68',

    opacity: .52,

  },

  ambientMessage: {

    right: '12%',

    top: '26%',

    display: 'flex',

    alignItems: 'center',

    gap: 10,

    width: 236,

    padding: '11px 13px',

    borderRadius: 14,

    color: '#777772',

    opacity: .42,

  },

  ambientQualified: {

    right: '15%',

    bottom: '17%',

    display: 'flex',

    alignItems: 'center',

    gap: 10,

    width: 226,

    padding: '10px 12px',

    borderRadius: 14,

    color: '#6d6d68',

    opacity: .48,

  },

  ambientEarnings: {

    left: '16%',

    bottom: '14%',

    width: 260,

    padding: '14px 15px',

    borderRadius: 16,

    color: '#666660',

    opacity: .34,

  },

  ambientCode: {

    right: '24%',

    top: '12%',

    width: 164,

    padding: '12px 14px',

    borderRadius: 13,

    color: '#777772',

    opacity: .30,

  },

  ambientAvatar: {

    width: 30,

    height: 30,

    flex: '0 0 30px',

    display: 'grid',

    placeItems: 'center',

    borderRadius: '50%',

    background: '#e5e5e1',

    color: '#858580',

    fontSize: 9,

    fontWeight: 750,

  },

  ambientTextStack: {

    display: 'grid',

    gap: 3,

    minWidth: 0,

    flex: 1,

  },

  ambientStatus: {

    minWidth: 34,

    height: 17,

    display: 'grid',

    placeItems: 'center',

    padding: '0 7px',

    borderRadius: 999,

    background: '#ecece8',

    color: '#92928d',

    fontSize: 6,

    fontWeight: 800,

    letterSpacing: '.06em',

  },

  ambientDot: {

    width: 8,

    height: 8,

    flex: '0 0 8px',

    borderRadius: '50%',

    background: '#d8d8d4',

  },

  ambientCheck: {

    width: 28,

    height: 28,

    flex: '0 0 28px',

    display: 'grid',

    placeItems: 'center',

    borderRadius: '50%',

    background: '#e5e5e1',

    color: '#7b7b76',

    fontSize: 10,

    fontWeight: 800,

  },

  ambientSmallLabel: {

    display: 'block',

    color: '#999994',

    fontSize: 7,

    fontWeight: 800,

    letterSpacing: '.08em',

  },

  ambientEarningsRow: {

    display: 'grid',

    gridTemplateColumns: 'auto 1px 1fr',

    alignItems: 'center',

    gap: 12,

    marginTop: 9,

  },

  landingContent: {

    position: 'relative',

    zIndex: 5,

    alignSelf: 'center',

    justifySelf: 'center',

    width: 'min(100%, 680px)',

    marginTop: -18,

    textAlign: 'center',

  },

  landingTitle: {

    margin: 0,

    color: '#171717',

    fontSize: 'clamp(32px, 3.4vw, 46px)',

    fontWeight: 720,

    lineHeight: 1.03,

    letterSpacing: '-.045em',

  },

  landingCopy: {

    maxWidth: 500,

    margin: '16px auto 0',

    color: '#62625d',

    fontSize: 'clamp(13px, 1.05vw, 15px)',

    lineHeight: 1.55,

  },

  landingActions: {

    marginTop: 24,

    display: 'flex',

    alignItems: 'center',

    justifyContent: 'center',

    gap: 14,

    flexWrap: 'wrap',

  },

  landingButton: {

    minWidth: 180,

    minHeight: 50,

    padding: '0 26px',

    border: '1px solid #151515',

    borderRadius: 999,

    background: '#151515',

    color: '#fff',

    cursor: 'pointer',

    fontFamily: 'inherit',

    fontSize: 13,

    fontWeight: 700,

  },

  landingEnterHint: {

    color: '#8a8a85',

    fontSize: 10,

  },

  landingFooter: {

    position: 'relative',

    zIndex: 6,

    minHeight: 36,

    marginTop: 'auto',

    padding: 0,

    display: 'flex',

    alignItems: 'center',

    justifyContent: 'space-between',

    gap: 24,

    color: '#a0a09b',

    boxSizing: 'border-box',

  },

  landingLegal: {

    fontSize: 9,

    fontWeight: 500,

  },

  landingSocials: {

    display: 'flex',

    alignItems: 'center',

    gap: 20,

  },

  landingSocialLink: {

    fontSize: '1.28rem',

    color: '#000',

    transition: 'color 0.2s ease',

    lineHeight: 1,

  },

  applicationFooter: {

    position: 'relative',

    zIndex: 8,

    minHeight: 36,

    marginTop: 'auto',

    padding: 0,

    display: 'flex',

    alignItems: 'center',

    justifyContent: 'space-between',

    gap: 24,

    color: '#a0a09b',

    boxSizing: 'border-box',

  },

  applicationLegal: {

    fontSize: 9,

    fontWeight: 500,

  },

  applicationSocials: {

    display: 'flex',

    alignItems: 'center',

    gap: 20,

  },

  applicationSocialLink: {

    fontSize: '1.28rem',

    color: '#000',

    transition: 'color 0.2s ease',

    lineHeight: 1,

  },

  formPage: {

    minHeight: '100vh',

    display: 'flex',

    flexDirection: 'column',

    position: 'relative',

    overflowX: 'hidden',

    padding: '30px 44px 24px',

    background:

      'radial-gradient(circle at 50% 46%, rgba(246,246,244,.92) 0%, rgba(251,251,250,.68) 30%, rgba(255,255,255,0) 60%), #fff',

    color: '#151515',

    boxSizing: 'border-box',

    fontFamily:

      'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  },

  page: {

    minHeight: '100vh',

    display: 'flex',

    flexDirection: 'column',

    background: '#ffff',

    color: '#151515',

    position: 'relative',

    boxSizing: 'border-box',

    fontFamily:

      'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  },

  header: {

    height: 64,

    padding: '0 26px',

    display: 'flex',

    alignItems: 'center',

    justifyContent: 'space-between',

    boxSizing: 'border-box',

  },

  logoSvg: {

    width: 82,

    height: 'auto',

    display: 'block',

  },

  applicationHeader: {

    position: 'relative',

    zIndex: 8,

    minHeight: 56,

    display: 'flex',

    alignItems: 'center',

    justifyContent: 'space-between',

    gap: 24,

    padding: 0,

    boxSizing: 'border-box',

  },

  applicationHeaderLogo: {

    width: 92,

    height: 'auto',

    display: 'block',

    justifySelf: 'start',

  },

  applicationHeaderTitle: {

    justifySelf: 'end',

    color: '#111',

    fontSize: 12,

    fontWeight: 700,

    lineHeight: 1,

    letterSpacing: '.055em',

    whiteSpace: 'nowrap',

  },

  hero: {

    flex: 1,

    width: 'min(650px, calc(100% - 40px))',

    margin: '0 auto',

    padding: '12vh 0 72px',

  },

  eyebrow: {

    marginBottom: 14,

    fontSize: 10,

    fontWeight: 900,

    letterSpacing: '0.11em',

  },

  heroTitle: {

    maxWidth: 650,

    margin: 0,

    fontSize: 'clamp(34px, 4.8vw, 52px)',

    lineHeight: 1.06,

    letterSpacing: '-0.045em',

    fontWeight: 650,

  },

  heroCopy: {

    maxWidth: 560,

    margin: '20px 0 0',

    fontSize: 'clamp(16px, 1.6vw, 18px)',

    lineHeight: 1.5,

    color: '#4d4d4d',

  },

  heroActions: {

    marginTop: 26,

    display: 'flex',

    alignItems: 'center',

    gap: 14,

    flexWrap: 'wrap',

  },

  formAmbient: {

    position: 'absolute',

    zIndex: 1,

    inset: '86px 0 58px',

    overflow: 'hidden',

  },

  formAmbientCard: {

    position: 'absolute',

    display: 'flex',

    alignItems: 'center',

    gap: 9,

    padding: '10px 12px',

    borderRadius: 13,

    color: '#777772',

    opacity: .22,

  },

  formAmbientProspect: {

    left: '8%',

    top: '24%',

    width: 190,

  },

  formAmbientQualified: {

    right: '8%',

    bottom: '20%',

    width: 205,

  },

  formAmbientCode: {

    right: '13%',

    top: '18%',

    width: 150,

    display: 'grid',

    gap: 5,

    opacity: .16,

  },

  formAmbientAvatar: {

    width: 26,

    height: 26,

    flex: '0 0 26px',

    display: 'grid',

    placeItems: 'center',

    borderRadius: '50%',

    background: '#e4e4e0',

    color: '#8b8b85',

    fontSize: 8,

    fontWeight: 700,

  },

  formAmbientCheck: {

    width: 26,

    height: 26,

    flex: '0 0 26px',

    display: 'grid',

    placeItems: 'center',

    borderRadius: '50%',

    background: '#e4e4e0',

    color: '#83837e',

    fontSize: 9,

    fontWeight: 750,

  },

  formAmbientCopy: {

    display: 'grid',

    gap: 3,

    minWidth: 0,

  },

  formAmbientLabel: {

    color: '#999994',

    fontSize: 7,

    fontWeight: 800,

    letterSpacing: '.08em',

  },

  questionShell: {

    position: 'relative',

    zIndex: 3,

    flex: 1,

    width: '100%',

    display: 'grid',

    placeItems: 'center',

    boxSizing: 'border-box',

  },

  questionContent: {

    width: 'min(540px, calc(100% - 40px))',

    margin: '0 auto',

    padding: '7vh 0 118px',

    boxSizing: 'border-box',

    textAlign: 'left',

  },

  questionNumber: {

    width: '100%',

    margin: '0 0 10px',

    color: '#7d7d78',

    fontSize: 11,

    fontWeight: 700,

    letterSpacing: '.02em',

    textAlign: 'left',

  },

  questionTitle: {

    maxWidth: 500,

    margin: 0,

    color: '#171717',

    fontSize: 'clamp(28px, 3vw, 38px)',

    lineHeight: 1.08,

    letterSpacing: '-.04em',

    fontWeight: 650,

    textAlign: 'left',

  },

  questionDescription: {

    maxWidth: 460,

    margin: '10px 0 0',

    color: '#73736e',

    fontSize: 12,

    lineHeight: 1.55,

    textAlign: 'left',

  },

  answerArea: {

    width: '100%',

    marginTop: 28,

    textAlign: 'left',

  },

  nameGrid: {

    width: 'min(100%, 460px)',

    margin: 0,

    display: 'grid',

    gridTemplateColumns: '1fr',

    gap: 18,

  },

  fieldLabel: {

    display: 'block',

    marginBottom: 7,

    color: '#686864',

    fontSize: 10,

    fontWeight: 650,

    textAlign: 'left',

  },

  textInput: {

    width: 'min(100%, 460px)',

    height: 50,

    display: 'block',

    margin: 0,

    padding: '0 4px',

    border: 0,

    borderBottom: '1.5px solid #a9a9a4',

    borderRadius: 0,

    background: 'transparent',

    color: '#171717',

    outline: 'none',

    boxSizing: 'border-box',

    fontSize: 18,

    fontWeight: 450,

    textAlign: 'left',

  },

  iconInputWrap: {

    width: 'min(100%, 460px)',

    height: 50,

    display: 'flex',

    alignItems: 'center',

    gap: 9,

    margin: 0,

    borderBottom: '1.5px solid #a9a9a4',

    boxSizing: 'border-box',

  },

  inputLeadingIcon: {

    width: 17,

    height: 17,

    flex: '0 0 17px',

    color: '#777772',

    strokeWidth: 1.8,

  },

  iconTextInput: {

    flex: 1,

    minWidth: 0,

    height: '100%',

    padding: 0,

    border: 0,

    outline: 'none',

    background: 'transparent',

    color: '#171717',

    fontFamily: 'inherit',

    fontSize: 18,

    fontWeight: 450,

    textAlign: 'left',

    boxSizing: 'border-box',

  },

  prefixInputWrap: {

    width: 'min(100%, 460px)',

    height: 50,

    display: 'flex',

    alignItems: 'center',

    gap: 2,

    margin: 0,

    borderBottom: '1.5px solid #a9a9a4',

    boxSizing: 'border-box',

  },

  inputPrefix: {

    flex: '0 0 auto',

    color: '#171717',

    fontSize: 18,

    fontWeight: 450,

    lineHeight: 1,

  },

  prefixTextInput: {

    flex: 1,

    minWidth: 0,

    height: '100%',

    padding: 0,

    border: 0,

    outline: 'none',

    background: 'transparent',

    color: '#171717',

    fontFamily: 'inherit',

    fontSize: 18,

    fontWeight: 450,

    textAlign: 'left',

    boxSizing: 'border-box',

  },

  textarea: {

    width: 'min(100%, 460px)',

    minHeight: 124,

    display: 'block',

    margin: 0,

    padding: '12px 4px',

    border: 0,

    borderBottom: '1.5px solid #a9a9a4',

    borderRadius: 0,

    background: 'transparent',

    color: '#171717',

    outline: 'none',

    resize: 'vertical',

    boxSizing: 'border-box',

    fontFamily: 'inherit',

    fontSize: 16,

    lineHeight: 1.5,

    textAlign: 'left',

  },

  choiceList: {

    width: 'min(100%, 460px)',

    margin: 0,

    display: 'grid',

    gap: 8,

  },

  choiceButton: {

    width: '100%',

    minHeight: 44,

    padding: '7px 10px',

    display: 'flex',

    alignItems: 'center',

    gap: 9,

    textAlign: 'left',

    border: '1px solid rgba(21,21,21,0.28)',

    borderRadius: 6,

    background: 'rgba(255,255,255,0.38)',

    color: '#151515',

    cursor: 'pointer',

    fontFamily: 'inherit',

    transition: 'background 120ms ease, border-color 120ms ease',

  },

  choiceButtonSelected: {

    border: '2px solid #151515',

    background: 'rgba(21,21,21,0.08)',

  },

  choiceKey: {

    width: 24,

    height: 24,

    flex: '0 0 24px',

    display: 'inline-flex',

    alignItems: 'center',

    justifyContent: 'center',

    border: '1px solid rgba(21,21,21,0.3)',

    borderRadius: 4,

    background: '#fff',

    fontSize: 12,

    fontWeight: 850,

  },

  choiceKeySelected: {

    borderColor: '#151515',

    background: '#151515',

    color: '#fff',

  },

  choiceText: {

    flex: 1,

    fontSize: 14,

    lineHeight: 1.35,

    fontWeight: 550,

  },

  check: {

    fontSize: 16,

    fontWeight: 900,

  },

  primaryButton: {

    minHeight: 40,

    padding: '0 16px',

    border: '1px solid #151515',

    borderRadius: 7,

    background: '#151515',

    color: '#fff',

    cursor: 'pointer',

    fontFamily: 'inherit',

    fontSize: 14,

    fontWeight: 800,

  },

  primaryButtonDisabled: {

    opacity: 0.35,

    cursor: 'default',

  },

  enterHint: {

    fontSize: 11,

    color: '#686868',

  },

  desktopActions: {

    marginTop: 30,

    display: 'flex',

    alignItems: 'center',

    justifyContent: 'flex-start',

    gap: 10,

  },

  bottomControls: {

    display: 'none',

    position: 'fixed',

    left: 0,

    right: 0,

    bottom: 0,

    zIndex: 10,

    background: '#ffff',

  },

  bottomControlsInner: {

    width: 'min(650px, calc(100% - 40px))',

    minHeight: 68,

    margin: '0 auto',

    display: 'flex',

    alignItems: 'center',

    justifyContent: 'space-between',

    gap: 12,

  },

  backButton: {

    minWidth: 118,

    height: 44,

    padding: '0 18px',

    border: '1px solid #d7d7d3',

    borderRadius: 999,

    background: 'rgba(255,255,255,.76)',

    color: '#555550',

    cursor: 'pointer',

    fontFamily: 'inherit',

    fontSize: 11,

    fontWeight: 650,

  },

  continueButton: {

    minWidth: 152,

    height: 44,

    padding: '0 22px',

    border: '1px solid #151515',

    borderRadius: 999,

    background: '#151515',

    color: '#fff',

    cursor: 'pointer',

    fontFamily: 'inherit',

    fontSize: 11,

    fontWeight: 700,

  },

  continueButtonDisabled: {

    opacity: 0.35,

    cursor: 'default',

  },

  submitModalBackdrop: {

    position: 'fixed',

    zIndex: 1000,

    inset: 0,

    display: 'grid',

    placeItems: 'center',

    padding: 24,

    background: 'rgba(17,17,17,.18)',

    backdropFilter: 'blur(5px)',

    WebkitBackdropFilter: 'blur(5px)',

    boxSizing: 'border-box',

  },

  submitModal: {

    width: 'min(100%, 390px)',

    padding: '28px 28px 24px',

    border: '1px solid rgba(17,17,17,.08)',

    borderRadius: 22,

    background: '#fff',

    boxShadow: '0 24px 70px rgba(0,0,0,.12)',

    boxSizing: 'border-box',

  },

  submitModalEyebrow: {

    marginBottom: 12,

    color: '#8b8b86',

    fontSize: 9,

    fontWeight: 750,

    letterSpacing: '.08em',

  },

  submitModalTitle: {

    margin: 0,

    color: '#171717',

    fontSize: 28,

    fontWeight: 680,

    lineHeight: 1.05,

    letterSpacing: '-.035em',

  },

  submitModalCopy: {

    margin: '12px 0 0',

    color: '#6f6f6a',

    fontSize: 12,

    lineHeight: 1.55,

  },

  submitModalActions: {

    marginTop: 24,

    display: 'flex',

    alignItems: 'center',

    justifyContent: 'flex-end',

    gap: 8,

  },

  submitModalCancelButton: {

    minWidth: 92,

    height: 40,

    padding: '0 16px',

    border: '1px solid #d7d7d3',

    borderRadius: 999,

    background: '#fff',

    color: '#555550',

    cursor: 'pointer',

    fontFamily: 'inherit',

    fontSize: 11,

    fontWeight: 650,

  },

  submitModalConfirmButton: {

    minWidth: 118,

    height: 40,

    padding: '0 18px',

    border: '1px solid #151515',

    borderRadius: 999,

    background: '#151515',

    color: '#fff',

    cursor: 'pointer',

    fontFamily: 'inherit',

    fontSize: 11,

    fontWeight: 700,

  },

  errorBox: {

    width: 'fit-content',

    maxWidth: 'min(100%, 360px)',

    margin: '16px 0 0',

    padding: '9px 11px',

    border: '1px solid #d5a7a7',

    borderRadius: 6,

    background: '#fff1f1',

    color: '#861f1f',

    fontSize: 14,

    fontWeight: 700,

  },

  successShell: {

    flex: 1,

    width: 'min(100%, 620px)',

    margin: '0 auto',

    display: 'grid',

    placeContent: 'center',

    justifyItems: 'center',

    padding: '48px 22px 120px',

    textAlign: 'center',

    boxSizing: 'border-box',

  },

  successMark: {

    width: 42,

    height: 42,

    marginBottom: 18,

    display: 'grid',

    placeItems: 'center',

    borderRadius: '50%',

    background: '#151515',

    color: '#fff',

    fontSize: 18,

    fontWeight: 800,

  },

  successTitle: {

    margin: 0,

    color: '#171717',

    fontSize: 'clamp(32px, 3.4vw, 46px)',

    fontWeight: 720,

    lineHeight: 1.03,

    letterSpacing: '-.045em',

  },

  successCopy: {

    maxWidth: 500,

    margin: '16px auto 0',

    color: '#62625d',

    fontSize: 'clamp(13px, 1.05vw, 15px)',

    lineHeight: 1.55,

  },

  successButton: {

    minWidth: 160,

    height: 46,

    marginTop: 24,

    padding: '0 22px',

    display: 'inline-flex',

    alignItems: 'center',

    justifyContent: 'center',

    border: '1px solid #151515',

    borderRadius: 999,

    background: '#151515',

    color: '#fff',

    textDecoration: 'none',

    boxSizing: 'border-box',

    fontSize: 12,

    fontWeight: 700,

  },

};
